/* In-place search drawer with predictive suggestions.
   Hooks the theme's existing header search icons, so no header markup changed. */
(function () {
  'use strict';

  var root = document.querySelector('[data-tmws-search]');
  if (!root) return;

  var panel    = root.querySelector('.tmws-search__panel');
  var input    = root.querySelector('[data-tmws-search-input]');
  var results  = root.querySelector('[data-tmws-search-results]');
  var hint     = root.querySelector('[data-tmws-search-hint]');
  var status   = root.querySelector('[data-tmws-search-status]');
  var clearBtn = root.querySelector('[data-tmws-search-clear]');
  var form     = root.querySelector('.tmws-search__form');

  var SUGGEST  = root.dataset.suggestUrl || '/search/suggest';
  var SEARCHU  = root.dataset.searchUrl || '/search';
  var SECTION  = root.dataset.sectionId;

  var lastFocus = null;
  var controller = null;
  var timer = null;
  var activeIndex = -1;

  /* ---------- open / close ---------- */
  function open(e, trigger) {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!root.hidden) return;
    // remember the icon that opened this, so focus can go back to it on close
    lastFocus = trigger || document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    // let the browser paint once so the transition runs
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    setTimeout(function () { input.focus(); input.select(); }, 60);
  }

  function close() {
    if (root.hidden) return;
    root.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    abort();
    setTimeout(function () {
      root.hidden = true;
      results.innerHTML = '';
      activeIndex = -1;
      input.setAttribute('aria-expanded', 'false');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }, 200);
  }

  var requestSeq = 0;
  var cache = Object.create(null);   /* term -> rendered html, so backspacing is instant */
  var cacheKeys = [];
  function cacheGet(k) { return cache[k]; }
  function cachePut(k, v) {
    if (!(k in cache)) {
      cacheKeys.push(k);
      if (cacheKeys.length > 30) delete cache[cacheKeys.shift()];
    }
    cache[k] = v;
  }
  function paint(html) {
    results.innerHTML = html;
    activeIndex = -1;
    hint.hidden = true;
    input.setAttribute('aria-expanded', 'true');
    announce();
  }
  function abort() {
    if (controller) {
      try { controller.abort(); } catch (e) { /* older browsers */ }
      controller = null;
    }
  }
  function isAbort(err) {
    return !!err && (err.name === 'AbortError' || err.code === 20);
  }

  /* ---------- fetching ---------- */
  function fetchSuggestions(term) {
    var hit = cacheGet(term);
    if (hit !== undefined) { abort(); requestSeq++; panel.classList.remove('is-busy'); paint(hit); return; }
    abort();
    var seq = ++requestSeq;
    if (!('AbortController' in window)) { controller = null; }
    else { controller = new AbortController(); }

    var url = SUGGEST
      + '?q=' + encodeURIComponent(term)
      + '&resources[type]=product,collection'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=last'
      + '&section_id=' + encodeURIComponent(SECTION);

    panel.classList.add('is-busy');

    var request;
    try {
      request = fetch(url, controller ? { signal: controller.signal } : undefined);
    } catch (e) {
      panel.classList.remove('is-busy');
      return;
    }
    request
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // a slower earlier request must never overwrite a newer one
        if (seq !== requestSeq) return;
        // the section endpoint returns the section wrapped in a div
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('.shopify-section') || doc.body;
        var markup = inner.innerHTML.trim();
        cachePut(term, markup);
        panel.classList.remove('is-busy');
        paint(markup);
      })
      .catch(function (err) {
        if (isAbort(err) || seq !== requestSeq) return;
        panel.classList.remove('is-busy');
        // network trouble should never trap the shopper in the drawer
        results.innerHTML = '';
        hint.hidden = false;
      });
  }

  function announce() {
    if (!status) return;
    var n = results.querySelectorAll('.tmws-ps__result').length;
    status.textContent = n ? n + ' suggestions' : 'No suggestions';
  }

  /* ---------- typing ---------- */
  function onInput() {
    var term = input.value.trim();
    clearBtn.hidden = term.length === 0;

    if (timer) clearTimeout(timer);
    if (term.length < 2) {
      abort();
      results.innerHTML = '';
      hint.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      return;
    }
    timer = setTimeout(function () { fetchSuggestions(term); }, 180);
  }

  /* ---------- keyboard ---------- */
  function items() { return Array.prototype.slice.call(results.querySelectorAll('.tmws-ps__result')); }

  function move(delta) {
    var list = items();
    if (!list.length) return;
    if (activeIndex >= 0 && list[activeIndex]) list[activeIndex].classList.remove('is-active');
    activeIndex += delta;
    if (activeIndex < 0) activeIndex = list.length - 1;
    if (activeIndex >= list.length) activeIndex = 0;
    var el = list[activeIndex];
    el.classList.add('is-active');
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  root.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); return; }
    if (e.key === 'Enter') {
      var list = items();
      if (activeIndex >= 0 && list[activeIndex]) {
        e.preventDefault();
        window.location.href = list[activeIndex].getAttribute('href');
      }
      // otherwise the form submits to /search on its own
    }
    if (e.key === 'Tab') {
      // keep focus inside the drawer
      var focusables = panel.querySelectorAll('input,button,a[href]');
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  form.addEventListener('submit', function (e) {
    if (!input.value.trim()) e.preventDefault();
  });

  input.addEventListener('input', onInput);
  clearBtn.addEventListener('click', function () {
    input.value = '';
    clearBtn.hidden = true;
    results.innerHTML = '';
    hint.hidden = false;
    input.focus();
  });

  root.querySelectorAll('[data-tmws-search-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });

  /* ---------- take over the theme's existing search triggers ---------- */
  var TRIGGERS = '.js-search-destop, .js-search-mobile, a.search, .js-box-search-open, [data-tmws-search-open]';
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest(TRIGGERS) : null;
    if (!t) return;
    // ignore anything inside our own drawer
    if (root.contains(t)) return;
    open(e, t);
  }, true); // capture, so the theme's own handler never runs

  /* the legacy drawer must not appear behind ours */
  var legacy = document.querySelector('.search-full-destop');
  if (legacy) legacy.setAttribute('hidden', 'hidden');
  document.addEventListener('click', function () {
    document.body.classList.remove('activedestop');
  }, true);

  /* deep link: /?tmws-search=1 or #search opens it */
  if (/[?&]tmws-search=1/.test(location.search) || location.hash === '#search') open();
})();
